import React from 'react';
import {
  Clock, Settings, LayoutDashboard, LogOut, User, ChevronDown, Maximize, Minimize
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { ThemeToggle } from '../../components/ThemeToggle';
import { LogoBrain } from '../../components/Logo';

export const POSHeader = ({
  user, isAdmin, businessType, selectedIndustry, INDUSTRY_FEATURES,
  handleIndustrySwitch, currentTime, currentShift, terminals, selectedTerminal,
  setShowShiftDialog, setShowAgeVerifyDialog, toggleFullscreen, isFullscreen,
  navigate, handleLogout,
}) => (
  <header className="h-14 bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 border-b border-slate-700 flex items-center justify-between px-4 flex-shrink-0">
    {/* Left: Logo & Business Type */}
    <div className="flex items-center gap-4">
      <div className="flex items-center gap-3">
        <LogoBrain size={40} animate={true} />
        <div>
          <h1 className="text-white font-bold text-lg leading-none">FlowFeed POS</h1>
          <p className="text-[10px] text-slate-400 uppercase tracking-wider">Point of Sale</p>
        </div>
      </div>
      
      {isAdmin ? (
        <Select value={selectedIndustry} onValueChange={handleIndustrySwitch}>
          <SelectTrigger className="w-48 h-9 border-slate-600 bg-slate-800/50 text-white" data-testid="industry-selector">
            <SelectValue placeholder="Select Industry">
              {businessType && (
                <span className="flex items-center gap-2">
                  <span className="h-2 w-2 rounded-full bg-emerald-500"></span>
                  <span className="font-medium text-sm">{businessType.name}</span>
                </span>
              )}
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            {Object.entries(INDUSTRY_FEATURES).map(([id, industry]) => (
              <SelectItem key={id} value={id}>
                <span className="flex items-center gap-2">
                  <span className={`h-2 w-2 rounded-full bg-${industry.color}-500`}></span>
                  {industry.name}
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      ) : businessType && (
        <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-slate-700/50 border border-slate-600">
          <span className="h-2 w-2 rounded-full bg-emerald-500"></span>
          <span className="font-medium text-sm text-white">{businessType.name}</span>
        </div>
      )}
    </div>

    {/* Center: Time & Shift Info */}
    <div className="flex items-center gap-6">
      <div className="text-center">
        <p className="text-2xl font-mono font-bold text-white">
          {currentTime.toLocaleTimeString('en-US', { hour12: false })}
        </p>
        <p className="text-xs text-slate-400">
          {currentTime.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
        </p>
      </div>
      
      <div className="h-8 w-px bg-slate-700" />
      
      {currentShift?.id ? (
        <div className="flex items-center gap-2">
          <span className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />
          <span className="text-sm text-emerald-400 font-medium">Shift Active</span>
          <span className="text-xs text-slate-500">
            {terminals.find(t => t.id === selectedTerminal)?.name || `#${currentShift.shift_number || 'N/A'}`}
          </span>
        </div>
      ) : (
        <Button 
          onClick={() => setShowShiftDialog(true)} 
          size="sm"
          className="bg-amber-600 hover:bg-amber-700 text-white"
        >
          <Clock className="h-4 w-4 mr-1" /> Open Shift
        </Button>
      )}
    </div>

    {/* Right: Actions & User Menu */}
    <div className="flex items-center gap-2">
      {businessType?.requiresAgeVerify && (
        <Button 
          variant="ghost" size="sm"
          onClick={() => setShowAgeVerifyDialog(true)}
          className="text-amber-400 hover:text-amber-300 hover:bg-slate-700"
          data-testid="age-verify-btn"
        >
          <User className="h-4 w-4 mr-1" />
          <span className="hidden sm:inline">21+ Verify</span>
        </Button>
      )}
      
      <ThemeToggle />
      
      <Button variant="ghost" size="icon" className="text-slate-400 hover:text-white hover:bg-slate-700"
        onClick={toggleFullscreen} data-testid="fullscreen-btn">
        {isFullscreen ? <Minimize className="h-5 w-5" /> : <Maximize className="h-5 w-5" />}
      </Button>
      
      <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard')}
        className="text-slate-300 hover:text-white hover:bg-slate-700" data-testid="switch-to-admin-btn">
        <LayoutDashboard className="h-4 w-4 mr-1" />
        <span className="hidden sm:inline">Dashboard</span>
      </Button>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" className="flex items-center gap-2 text-white hover:bg-slate-700 px-2">
            <div className="h-8 w-8 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-white font-bold text-sm">
              {user?.full_name?.charAt(0) || user?.email?.charAt(0) || 'U'}
            </div>
            <div className="text-left hidden md:block">
              <p className="text-sm font-medium leading-none">{user?.full_name || 'User'}</p>
              <p className="text-[10px] text-slate-400 capitalize">{user?.role || 'Staff'}</p>
            </div>
            <ChevronDown className="h-4 w-4 text-slate-400" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-48">
          <DropdownMenuItem onClick={() => navigate('/settings')}>
            <Settings className="h-4 w-4 mr-2" /> Settings
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => navigate('/dashboard')}>
            <LayoutDashboard className="h-4 w-4 mr-2" /> Admin Dashboard
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={handleLogout} className="text-red-600">
            <LogOut className="h-4 w-4 mr-2" /> Logout
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  </header>
);
